import {
  ChevronLeftIcon,
  ClockIcon,
  TagIcon,
  XIcon,
} from '@heroicons/react/outline';
import { Button, Checkbox, Form, Input } from 'antd';
import CrownIcon from 'assets/Icons/CrownIcon';
import TickOnboarding from 'assets/Icons/TickOnboarding';
import confetti from 'canvas-confetti';
import disposable from 'disposable-email';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import IntlTelInput from 'react-intl-tel-input';
import { useRecoilState } from 'recoil';

import 'react-phone-number-input/style.css';
import 'react-intl-tel-input/dist/main.css';

import { useAuth } from '@/hooks/useAuth';
import { EventDetails } from '@/hooks/useClickEventTracking';
import { getScreenName } from '@/hooks/usePageTracking';

import { calculatePasswordStrengthV2 } from '@/components/Auth/SignUp';
import CheckoutV2Card from '@/components/CheckoutV2/Components/Card';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import {
  platformRazorpayCloseModalEvent,
  platformSubscriptionCheckoutEvent,
  platformSubscriptionSuccessfulEvent,
} from '@/components/shared/Gtags';
import { LoadingV2 } from '@/components/shared/LoadingV2';
import { notify } from '@/components/shared/notify';

import { authModalControl, tokenUpdate } from '@/atoms/authModalControl';
import { getGlobalCountry } from '@/atoms/countryState';
import { user } from '@/atoms/user';
import appwriteService from '@/config/appwrite';
import { getSubPlanNameLog } from '@/pages/gems-transactions';
import { checkEmailExist } from '@/services/Cart';
import {
  applySubscriptionOffer,
  plans,
  startSubscription,
  verifySubscription,
} from '@/services/Subscribe';
import { getUserProfile } from '@/services/user';
import { isPlatformSubscriptionActive } from '@/utils/index';
import {
  IPlan,
  IStartSubscriptionBody,
  ISubscriptionOffer,
} from '@/utils/interfaces/Subscription';
import { User } from '@/utils/interfaces/User';
import {
  calculateDaysFromInterval,
  globalSubscriptionEvent,
} from '@/utils/Subscription';

const resolveCountryIso = (
  countryOrDial?: string | null,
  fallbackIso = 'IN',
): CountryCode => {
  if (!countryOrDial) return fallbackIso as CountryCode;
  const clean = countryOrDial.trim();
  if (/^[A-Za-z]{2}$/.test(clean)) {
    return clean.toUpperCase() as CountryCode;
  }
  const digits = clean.replace(/\D/g, '');
  if (!digits) return fallbackIso as CountryCode;
  if (digits === '91') return 'IN';
  if (digits === '1') return 'US';
  try {
    const countries = getCountries();
    for (const c of countries) {
      if (getCountryCallingCode(c) === digits) return c;
    }
  } catch {
    // ignore
  }
  return fallbackIso as CountryCode;
};

const PlatformCheckout = () => {
  const router = useRouter();
  const { planId, coupon: urlCoupon } = router.query;

  const [form] = Form.useForm();
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [, setAuthModal] = useRecoilState(authModalControl);
  const { onSignUp } = useAuth();
  const globalCountry = getGlobalCountry();

  const formValues = Form.useWatch([], form);
  const watchedEmail = Form.useWatch('email', form);
  const [submittable, setSubmittable] = useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);

  const [token, setToken] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState<
    'card' | 'upi' | 'continue' | null
  >(null);
  const [subscriptionSuccess, setSubscriptionSuccess] =
    useState<boolean>(false);

  const [planList, setPlanList] = useState<IPlan[]>();
  const [selectedPlan, setSelectedPlan] = useState<IPlan>();

  // Promo code & Offer states
  const [couponInput, setCouponInput] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [activeOffer, setActiveOffer] = useState<ISubscriptionOffer | null>(
    null,
  );
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState<boolean>(false);
  const [isGst, setIsGst] = useState<boolean>(false);
  const [tokenUpdateState] = useRecoilState(tokenUpdate);

  // Form & Auth states
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [emailAlreadyExists, setEmailAlreadyExists] = useState<boolean>(false);
  const [phoneCountry, setPhoneCountry] = useState<string>('in');

  const phoneValidRef = useRef<boolean>(false);
  const hasPrefilledUserRef = useRef<number | string | null>(null);
  const formItemPrefixCls = 'checkout-v2-item';

  // ─── Fetch Plan Data & Logged-in Status ──────────────────────────────────
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const result = await plans({ type: 'platform', group_name: 'main' });
      setPlanList(result.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthStatus = async () => {
    try {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setToken(loggedInStatus);
    } catch (error) {
      console.error('Error fetching auth token:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    if (userData) {
      setToken(true);
    }
    fetchAuthStatus();
  }, [userData, tokenUpdateState]);

  // Set selected plan from query param
  useEffect(() => {
    if (router.isReady && !loading) {
      if (planList && planId) {
        const current = planList.find((p) => String(p.id) === String(planId));
        if (current) {
          setSelectedPlan(current);
        } else {
          router.replace('/404');
        }
      } else if (!planId && planList && planList.length > 0) {
        setSelectedPlan(planList[0]);
      } else if (!loading && !planList) {
        router.replace('/404');
      }
    }
  }, [planList, planId, loading, router]);

  // Prefill user details if logged in (only once per user ID to keep fields editable)
  useEffect(() => {
    if (userData && hasPrefilledUserRef.current !== userData.id) {
      hasPrefilledUserRef.current = userData.id;
      setToken(true);
      const rawPhone = userData.phone || '';
      let nationalPhone = rawPhone;

      const userCountryCodeOrDial =
        userData.country_code ||
        globalCountry?.countryCode ||
        globalCountry?.phoneCode ||
        'in';
      const defaultIso = resolveCountryIso(userCountryCodeOrDial, 'IN');
      let countryCode = defaultIso.toLowerCase();
      let phonePrefix = '91';
      try {
        phonePrefix = getCountryCallingCode(defaultIso);
      } catch {
        phonePrefix = globalCountry?.phoneCode
          ? String(globalCountry.phoneCode).replace(/\D/g, '')
          : '91';
      }

      if (rawPhone) {
        try {
          const raw = String(rawPhone).trim();
          const parsed = raw.startsWith('+')
            ? parsePhoneNumberFromString(raw)
            : parsePhoneNumberFromString(raw, defaultIso);

          if (parsed && (parsed.isValid() || parsed.nationalNumber)) {
            nationalPhone = parsed.nationalNumber;
            if (parsed.country) countryCode = parsed.country.toLowerCase();
            if (parsed.countryCallingCode)
              phonePrefix = String(parsed.countryCallingCode);
            phoneValidRef.current = true;
            setIsPhoneValid(true);
          } else {
            const digitsOnly = raw.replace(/\D/g, '');
            nationalPhone = digitsOnly;
            phoneValidRef.current = digitsOnly.length >= 8;
            setIsPhoneValid(digitsOnly.length >= 8);
          }
        } catch {
          phoneValidRef.current = true;
          setIsPhoneValid(true);
        }
      } else {
        phoneValidRef.current = false;
        setIsPhoneValid(false);
      }

      setPhoneCountry(countryCode);

      form.setFieldsValue({
        fullname: userData.name || '',
        phone: nationalPhone,
        phonePrefix,
        phoneCountryCode: countryCode,
        email: userData.email || '',
      });

      // Immediately validate fields once form is prefilled so button activates without requiring input change
      setTimeout(() => {
        form
          .validateFields({ validateOnly: true })
          .then(() => {
            setSubmittable(true);
          })
          .catch(() => { });
      }, 50);
    }
  }, [userData, form]);

  // Confetti animation
  const fireConfetti = () => {
    const canvas = document.getElementById(
      'coupon-confetti',
    ) as HTMLCanvasElement | null;
    if (canvas) {
      const myConfetti = confetti.create(canvas, {
        resize: true,
        useWorker: true,
      });
      myConfetti({
        particleCount: 50,
        spread: 60,
        startVelocity: 25,
        origin: { x: 0.7, y: 0.5 },
        colors: ['#ffffff', '#FFD700', '#FF69B4', '#87CEEB', '#32CD32'],
      });
    }
  };

  const getApiErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.response?.data?.errors) {
      const errValues = Object.values(error.response.data.errors).flat();
      if (errValues.length > 0) return String(errValues[0]);
    }
    return formateErrorObject(error) || 'Invalid promo code for this plan';
  };

  // ─── Apply Offer Function (Checks subscription_offers) ──────
  const applyOfferByCode = async (
    code: string,
    targetPlan?: IPlan,
  ): Promise<boolean> => {
    const planToUse = targetPlan || selectedPlan;
    if (!planToUse || !code || !code.trim()) return false;

    const offers: ISubscriptionOffer[] = planToUse.subscription_offers || [];
    const matched = offers.find(
      (o) =>
        o.code?.toUpperCase() === code.trim().toUpperCase() &&
        o.is_active !== false,
    );

    if (matched) {
      // User is authenticated or not: apply coupon locally and show discount from plan's subscription offers
      setActiveOffer(matched);
      setAppliedCoupon(matched.code);
      setCouponInput('');
      setCouponError(null);
      fireConfetti();
      // Note: No success toast on apply per user instruction
      return true;
    } else {
      setCouponError('Invalid promo code for this plan');
      return false;
    }
  };

  // Check URL query param coupon (e.g. ?coupon=FREE) when plan is loaded
  useEffect(() => {
    if (router.isReady && selectedPlan) {
      const codeFromUrl =
        (urlCoupon as string) || (router.query.coupon as string);
      if (codeFromUrl && !appliedCoupon) {
        applyOfferByCode(codeFromUrl, selectedPlan);
      }
    }
  }, [router.isReady, selectedPlan, urlCoupon, router.query.coupon]);

  // ─── Nitro SDK Page View Tracking ─────────────────────────────────────────
  useEffect(() => {
    if (!selectedPlan) return;
    const currentUrl = window.location.href;
    const utmData: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        utmData[key] = value;
      }
    });
    const zebra_selected_country = JSON.parse(
      (localStorage.getItem('zebra_selected_country') as string) || '{}',
    )?.value?.country;

    const run = async () => {
      try {
        const sendProductView = () => {
          if (
            typeof (window as any).nitro !== 'undefined' &&
            typeof (window as any).nitro.track === 'function' &&
            selectedPlan
          ) {
            const data = {
              page_url: currentUrl,
              plan_id: planId,
              plan_name: selectedPlan?.name,
              plan_price: selectedPlan?.razorpay_plan?.amount / 100,
              plan_cycle: calculateDaysFromInterval(
                selectedPlan?.razorpay_plan?.interval_count,
                selectedPlan?.razorpay_plan?.interval_unit,
              ),
              platform: 'website',
              country: zebra_selected_country?.name,
              currency: zebra_selected_country?.currency,
              utm_data: utmData,
            };
            (window as any).nitro.track(
              'platform_subscription_checkout_view',
              data,
            );
          }
        };

        if (
          typeof (window as any).nitro !== 'undefined' &&
          typeof (window as any).nitro.productView === 'function'
        ) {
          sendProductView();
        } else {
          (window as any).nitroSettings = (window as any).nitroSettings || {};
          const existingReady = (window as any).nitroSettings.ready;
          (window as any).nitroSettings.ready = function (...args: any[]) {
            if (typeof existingReady === 'function') {
              existingReady.apply(this, args);
            }
            sendProductView();
          };
        }
      } catch (error) {
        console.error('Nitro Product View Error:', error);
      }
    };

    run();
  }, [selectedPlan, planId]);

  // ─── Analytics & Tracking Helpers ────────────────────────────────────────
  const pathname = router.pathname;
  const physical =
    selectedPlan?.features[0]?.periodicity &&
    selectedPlan?.rules[0]?.physical;

  const platformSubscriptionCheckoutNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: number;
    plan_cycle: number;
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;
        const zebra_selected_country = JSON.parse(
          (localStorage.getItem('zebra_selected_country') as string) || '{}',
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) utmData[key] = value;
        });

        (window as any).nitro.track('platform_subscription_checkout', {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: data.plan_price / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const platformSubscriptionSuccessfulNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: string;
    plan_cycle: number;
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;
        const zebra_selected_country = JSON.parse(
          (localStorage.getItem('zebra_selected_country') as string) || '{}',
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) utmData[key] = value;
        });

        (window as any).nitro.track('platform_subscription_successful', {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: parseInt(data.plan_price) / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const platformSubscriptionFailedNitroEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: string;
    plan_cycle: number;
  }) => {
    try {
      if (
        typeof (window as any).nitro !== 'undefined' &&
        typeof (window as any).nitro.track === 'function'
      ) {
        const currentUrl = window.location.href;
        const zebra_selected_country = JSON.parse(
          (localStorage.getItem('zebra_selected_country') as string) || '{}',
        )?.value?.country;
        const utmData: Record<string, string> = {};
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.forEach((value, key) => {
          if (key.startsWith('utm_')) utmData[key] = value;
        });

        (window as any).nitro.track('platform_subscription_failed', {
          page_url: currentUrl,
          plan_id: data.plan_id,
          plan_price: parseInt(data.plan_price) / 100,
          plan_cycle: data.plan_cycle,
          platform: 'website',
          country: zebra_selected_country?.name,
          currency: zebra_selected_country?.currency,
          utm_data: utmData,
          status: 'failed',
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const triggerCheckoutEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_price: number;
    plan_cycle: number;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'platform_subscription_checkout',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: data.plan_id.toString(),
        sub_source_name: getSubPlanNameLog(selectedPlan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(selectedPlan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(selectedPlan)?.meta,
        },
      };

      platformSubscriptionCheckoutNitroEvent(data);
      platformSubscriptionCheckoutEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const triggerSuccessEvent = (data: {
    subscription_id: string;
    plan_cycle: number;
    plan_id: number;
    plan_price: string;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'platform_subscription_successful',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data.subscription_id,
        sub_source: 'plan',
        sub_source_id: selectedPlan?.id.toString(),
        sub_source_name: getSubPlanNameLog(selectedPlan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(selectedPlan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(selectedPlan)?.meta,
        },
      };

      platformSubscriptionSuccessfulNitroEvent(data);
      platformSubscriptionSuccessfulEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const triggerRazorpayCloseModalEvent = (data: {
    subscription_id: string;
    plan_id: number;
    plan_cycle: number;
    plan_price: string;
  }) => {
    try {
      const eventParams: EventDetails = {
        event_name: 'platform_subscription_failed',
        source: 'subscription',
        source_type: physical ? 'hardcopy' : 'digitalcopy',
        source_id: data?.subscription_id.toString(),
        sub_source: 'plan',
        sub_source_id: selectedPlan?.id.toString(),
        sub_source_name: getSubPlanNameLog(selectedPlan?.name ?? ''),
        source_name: 'platform_subscription',
        ...globalSubscriptionEvent(selectedPlan)?.event,
        meta: {
          click_source: getScreenName(pathname),
          ...globalSubscriptionEvent(selectedPlan)?.meta,
        },
      };

      platformSubscriptionFailedNitroEvent(data);
      platformRazorpayCloseModalEvent({
        payload: eventParams,
        userData,
        pathname,
      });
    } catch (e) {
      console.error(e);
    }
  };

  // ─── Razorpay Script & Payment Initiation ─────────────────────────────────
  const loadRazorpayScript = () => {
    return new Promise<boolean>((resolve) => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribePayment = async (paymentMethod?: 'card' | 'upi') => {
    if (!selectedPlan?.id) return;
    try {
      setLoadingPaymentMethod(paymentMethod || 'continue');
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        notify('Razorpay SDK failed to load. Are you online?', 'error');
        setLoadingPaymentMethod(null);
        return;
      }

      const formValues = form.getFieldsValue();
      const enteredGst = formValues.gstNumber?.trim()?.toUpperCase();

      // Add offer_id and payment_method extra payload if an offer is active
      const subData: IStartSubscriptionBody = {
        plan_id: selectedPlan.id,
        change: false,
        immediate: false,
        mode: 'auto',
        ...(isGst && enteredGst ? { gst_number: enteredGst } : {}),
        ...(activeOffer?.id
          ? {
            offer_id: activeOffer.id,
            payment_method:
              activeOffer.payment_method || paymentMethod || 'upi',
          }
          : {}),
      };

      if (isPlatformSubscriptionActive(userData)) {
        subData.change = true;
        subData.immediate = true;
      }

      const result = await startSubscription(subData);

      triggerCheckoutEvent({
        plan_id: selectedPlan.id,
        subscription_id: result?.data?.subscription_id?.toString() || '',
        plan_price: selectedPlan.razorpay_plan?.amount as number,
        plan_cycle: calculateDaysFromInterval(
          selectedPlan.periodicity || 0,
          selectedPlan.periodicity_type || '',
        ),
      });

      if (!process.env.NEXT_PUBLIC_RAZORPAY_KEY_2) {
        throw new Error('Missing RAZORPAY_KEY');
      }

      const options = {
        key: process.env.NEXT_PUBLIC_RAZORPAY_KEY_2 || '',
        ...(result.data.mode === 'auto'
          ? { subscription_id: result.data.razorpay.subscription_id }
          : {}),
        order_id: result.data.razorpay.order_id,
        name: 'Zebralearn',
        description: 'Subscription',
        prefill: {
          name: formValues.fullname?.trim() || userData?.name,
          email: userData?.email || formValues.email,
          contact: formValues.phone?.trim() || userData?.phone,
          ...(paymentMethod ? { method: paymentMethod } : {}),
        },
        handler: async function (response: {
          razorpay_payment_id: string;
          razorpay_subscription_id: string;
          razorpay_signature: string;
        }) {
          try {
            setIsVerifyingPayment(true);
            setLoadingPaymentMethod(paymentMethod || 'continue');
            triggerSuccessEvent({
              subscription_id:
                result?.data?.subscription_id?.toString() || '',
              plan_price: selectedPlan.razorpay_plan?.amount?.toString() || '',
              plan_cycle: calculateDaysFromInterval(
                selectedPlan.periodicity || 0,
                selectedPlan.periodicity_type || '',
              ),
              plan_id: selectedPlan.id,
            });

            const verifyWithRetry = async () => {
              let shouldRetry = true;
              let verifyResult: any;
              while (shouldRetry) {
                verifyResult = await verifySubscription({
                  rp_payment_id: response.razorpay_payment_id,
                  ...(result.data.mode === 'auto'
                    ? {
                      razorpay_subscription_id:
                        response.razorpay_subscription_id,
                    }
                    : {}),
                });
                const retry = verifyResult?.data?.retry;
                const retryAfter = verifyResult?.data?.retry_after;
                if (retry && retryAfter) {
                  await new Promise((res) =>
                    setTimeout(res, retryAfter * 1000),
                  );
                  continue;
                }
                shouldRetry = false;
              }
              return verifyResult;
            };

            await verifyWithRetry();
            setSubscriptionSuccess(true);
            const userProfile = await getUserProfile();
            setUserData(userProfile?.data);
          } catch (error: any) {
            notify(formateErrorObject(error), 'error');
            setLoadingPaymentMethod(null);
            setIsVerifyingPayment(false);
          }
        },
        modal: {
          ondismiss: function () {
            triggerRazorpayCloseModalEvent({
              plan_id: selectedPlan.id,
              subscription_id:
                result.data.mode === 'auto'
                  ? result.data.razorpay.subscription_id
                  : result.data.razorpay.order_id,
              plan_price: selectedPlan.razorpay_plan?.amount?.toString() || '',
              plan_cycle: calculateDaysFromInterval(
                selectedPlan.periodicity || 0,
                selectedPlan.periodicity_type || '',
              ),
            });
            setLoadingPaymentMethod(null);
            setIsVerifyingPayment(false);
          },
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      setLoadingPaymentMethod(null);
      setIsVerifyingPayment(false);
    }
  };

  // ─── Primary Payment Button Click Handler ─────────────────────────────────
  const handleInitiatePayment = async (method?: 'card' | 'upi') => {
    try {
      setLoadingPaymentMethod(method || 'continue');

      if (!token) {
        setAuthLoading(true);
        await form.validateFields();

        if (emailAlreadyExists) {
          setAuthModal((prevState) => ({
            ...prevState,
            login: true,
            email: form.getFieldValue('email')?.trim(),
            emailExistsMessage: true,
          }));
          setAuthLoading(false);
          setLoadingPaymentMethod(null);
          return;
        }

        const values = form.getFieldsValue();
        await onSignUp({
          email: values.email?.trim(),
          password: values.password,
          name: values.fullname?.trim(),
          phone: values.phone?.trim(),
          phoneCountryCode:
            values.phoneCountryCode ||
            globalCountry?.countryCode?.toLowerCase() ||
            'in',
          phonePrefix: values.phonePrefix || globalCountry?.phoneCode || '91',
          isModal: true,
          noRedirect: true,
        });

        await fetchAuthStatus();
        const profile = await getUserProfile();
        setUserData(profile?.data);
      } else {
        await form.validateFields();
      }

      // Apply coupon API gets called when someone presses to pay
      if (activeOffer && selectedPlan) {
        try {
          await applySubscriptionOffer({
            plan_id: selectedPlan.id,
            offer_id: activeOffer.id,
            payment_method: activeOffer.payment_method || method || 'upi',
          });
        } catch (error: any) {
          const errMsg = getApiErrorMessage(error);
          setCouponError(errMsg);
          setActiveOffer(null);
          setAppliedCoupon(null);
          setAuthLoading(false);
          setLoadingPaymentMethod(null);
          return;
        }
      }

      await handleSubscribePayment(method);
    } catch (error: any) {
      if (!error?.errorFields) {
        notify(formateErrorObject(error), 'error');
      }
      setLoadingPaymentMethod(null);
    } finally {
      setAuthLoading(false);
      // Note: setLoadingPaymentMethod(null) is intentionally NOT called here
      // because handleSubscribePayment opens Razorpay modal asynchronously (rzp.open() is non-blocking).
      // Each Razorpay callback (modal.ondismiss, handler error/success) manages the loading state.
    }
  };

  // ─── Email Existence Check ────────────────────────────────────────────────
  const handleCheckEmailExist = async () => {
    const emailVal = form.getFieldValue('email');
    if (!emailVal || token) return;

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
    const domain = emailVal.split('@')[1] || '';
    const isFakeEmail = domain ? !disposable.validate(domain) : false;

    if (!isValidEmail || isFakeEmail) {
      form.setFields([
        {
          name: 'email',
          errors: ['Please enter a valid email address!'],
        },
      ]);
      return;
    }

    try {
      await checkEmailExist(emailVal.trim());
      setEmailAlreadyExists(true);
      form.validateFields(['email']).catch(() => { });
      setAuthModal((prevState) => ({
        ...prevState,
        login: true,
        email: emailVal.trim(),
        emailExistsMessage: true,
      }));
    } catch {
      setEmailAlreadyExists(false);
      form.validateFields(['email']).catch(() => { });
    }
  };

  // Reactively check email existence when user finishes typing email
  useEffect(() => {
    if (token) {
      setEmailAlreadyExists(false);
      return;
    }

    const email = watchedEmail?.trim() ?? '';
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!isValidEmail) {
      setEmailAlreadyExists(false);
      return;
    }

    const domain = email.split('@')[1] || '';
    if (domain && !disposable.validate(domain)) {
      setEmailAlreadyExists(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        await checkEmailExist(email);
        setEmailAlreadyExists(true);
        form.validateFields(['email']).catch(() => { });
        setAuthModal((prevState) => ({
          ...prevState,
          login: true,
          email,
          emailExistsMessage: true,
        }));
      } catch {
        setEmailAlreadyExists(false);
        form.validateFields(['email']).catch(() => { });
      }
    }, 700);

    return () => clearTimeout(timer);
  }, [watchedEmail, token]);

  // ─── Coupon Code Handlers ─────────────────────────────────────────────────
  const handleApplyCoupon = async () => {
    if (!couponInput || !couponInput.trim()) {
      setCouponError('Please enter a promo code');
      return;
    }
    const code = couponInput.trim();
    setCouponLoading(true);
    try {
      await applyOfferByCode(code, selectedPlan);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setActiveOffer(null);
    setCouponError(null);
    notify('Coupon removed', 'info');
  };

  // ─── Pricing Calculations ────────────────────────────────────────────────
  const currentPrice = useMemo(() => {
    if (selectedPlan?.razorpay_plan?.amount) {
      return selectedPlan.razorpay_plan.amount / 100;
    }
    return 1999;
  }, [selectedPlan]);

  const strokedPrice = useMemo(() => {
    if (currentPrice === 1999) return 4800;
    return Math.round((currentPrice * 2.4) / 100) * 100;
  }, [currentPrice]);

  // Discount from activeOffer or manual coupon
  const { cartTotal, couponDiscount, totalDiscount, toPay } = useMemo(() => {
    let discount = 0;
    if (activeOffer) {
      if (activeOffer.discount_type === 'percentage') {
        discount = Math.round((currentPrice * activeOffer.discount_value) / 100);
        if (
          activeOffer.max_discount_amount &&
          discount > activeOffer.max_discount_amount
        ) {
          discount = activeOffer.max_discount_amount;
        }
      } else if (activeOffer.discount_type === 'flat') {
        discount = activeOffer.discount_value;
      } else {
        discount =
          activeOffer.discount_value <= 100
            ? Math.round((currentPrice * activeOffer.discount_value) / 100)
            : activeOffer.discount_value;
      }
    } else if (appliedCoupon) {
      discount = currentPrice === 1999 ? 400 : Math.round(currentPrice * 0.2);
    }

    const finalToPay = Math.max(0, currentPrice - discount);
    return {
      cartTotal: currentPrice,
      couponDiscount: discount,
      totalDiscount: discount,
      toPay: finalToPay,
    };
  }, [currentPrice, activeOffer, appliedCoupon]);

  const intervalCount = selectedPlan?.periodicity || 12;
  const intervalUnit =
    selectedPlan?.periodicity_type === 'yearly'
      ? intervalCount > 1
        ? 'Years'
        : 'Year'
      : intervalCount > 1
        ? 'Months'
        : 'Month';

  const offerPaymentMethod = useMemo(() => {
    const pm = activeOffer?.payment_method?.trim()?.toLowerCase();
    if (pm === 'upi' || pm === 'card') {
      return pm;
    }
    return undefined;
  }, [activeOffer]);

  const validityBadgeText = useMemo(() => {
    if (!selectedPlan) return 'Valid for 1 year only';
    const count = selectedPlan.periodicity || 12;
    const type = (selectedPlan.periodicity_type || '').toLowerCase();

    if (type === 'yearly') {
      return count === 1 ? 'Valid for 1 year only' : `Valid for ${count} years only`;
    }
    if (type === 'monthly') {
      if (count % 12 === 0) {
        const years = count / 12;
        return years === 1 ? 'Valid for 1 year only' : `Valid for ${years} years only`;
      }
      return count === 1 ? 'Valid for 1 month only' : `Valid for ${count} months only`;
    }
    if (type === 'quarterly') {
      return count === 1 ? 'Valid for 1 quarter only' : `Valid for ${count} quarters only`;
    }
    if (type === 'weekly') {
      return count === 1 ? 'Valid for 1 week only' : `Valid for ${count} weeks only`;
    }
    if (type === 'daily') {
      return count === 1 ? 'Valid for 1 day only' : `Valid for ${count} days only`;
    }
    return `Valid for ${count} ${intervalUnit.toLowerCase()} only`;
  }, [selectedPlan, intervalUnit]);

  // Form validity check: Ant Design form validation hook pattern to activate/disable payment buttons
  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        if (!token && emailAlreadyExists) {
          setSubmittable(false);
          return;
        }
        if (!phoneValidRef.current) {
          setSubmittable(false);
          return;
        }
        setSubmittable(true);
      })
      .catch(() => {
        setSubmittable(false);
      });
  }, [form, formValues, token, emailAlreadyExists, isGst, isPhoneValid, userData]);

  // ─── Render Loading Screen ───────────────────────────────────────────────
  if (loading || !planList || !selectedPlan) {
    return (
      <LoadingV2
        loaderColor='white'
        messages={[
          'Almost there, thanks for your patience!',
          'Getting everything set up for you.',
          'Your content is on its way!',
          "Just a sec, making sure everything's perfect.",
          "Hold on, we're almost ready!",
          "Loading... we'll be with you shortly.",
        ]}
      />
    );
  }

  // ─── Render Congratulations Success Screen ───────────────────────────────
  if (subscriptionSuccess) {
    return (
      <div className='min-h-[70vh] flex flex-col items-center justify-center bg-black px-4 py-16 text-center max-w-xl mx-auto font-montserrat'>
        <div className='flex justify-center mb-6'>
          <TickOnboarding width='80' />
        </div>
        <h2 className='text-2xl md:text-3xl font-medium text-white mb-3 flex items-center justify-center gap-2'>
          Congratulations <span>🎉</span>
        </h2>
        <p className='text-grayZl-400 text-sm md:text-base mb-8'>
          Subscription completed successfully. Unlimited learning is unlocked!
        </p>
        <Button
          type='primary'
          size='large'
          onClick={() => router.push('/books/')}
          className='bg-primary hover:opacity-90 h-11 px-8 rounded-xl font-medium text-white border-none'
        >
          Start Exploring
        </Button>
      </div>
    );
  }

  // ─── Reusable Plan Header Component ──────────────────────────────────────
  const PlanHeader = () => (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <span className='text-primary font-medium text-xs md:text-sm'>
          Selected Plan
        </span>
        <div
          className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border-none'
          style={{ backgroundColor: '#352311', color: '#F9BA5B' }}
        >
          <ClockIcon
            className='w-3.5 h-3.5 shrink-0'
            style={{ color: '#F9BA5B' }}
          />
          <span>{validityBadgeText}</span>
        </div>
      </div>

      <div className='flex items-center gap-2.5 pt-1'>
        <CrownIcon width={28} height={28} className='shrink-0' />
        <h2 className='text-2xl md:text-3xl font-bold uppercase tracking-wide text-[#DFB18B] font-montserrat'>
          {selectedPlan?.display_name || 'GOLD'}
        </h2>
      </div>

      <div className='flex items-baseline gap-2.5 pt-1'>
        <span className='line-through text-grayZl-700 font-medium text-lg md:text-xl'>
          ₹{(totalDiscount > 0 ? currentPrice : strokedPrice).toLocaleString('en-IN')}
        </span>
        <span className='text-3xl md:text-4xl font-bold text-white'>
          ₹{toPay.toLocaleString('en-IN')}
        </span>
        <span className='text-grayZl-600 text-xs md:text-sm font-normal'>
          / {intervalCount} {intervalUnit}
        </span>
      </div>
    </div>
  );

  // ─── Main Checkout View (Flex layout matching checkout-v2) ─────────────────
  return (
    <div className='p-4 pb-4 md:px-6 lg:px-8 md:pt-4 lg:pt-6 md:pb-0 lg:pb-0 font-montserrat text-white bg-black lg:min-h-[calc(100vh-60px)] xl:min-h-[calc(100vh-70px)] flex flex-col'>
      <div className='max-w-7xl mx-auto w-full flex-1 flex flex-col'>
        {/* Mobile-only Plan Header at the top */}
        <div className='lg:hidden mb-4'>
          <PlanHeader />
        </div>

        <div className='flex lg:flex-row flex-col flex-1 gap-6 lg:gap-8'>
          {/* ─── Left Column: Back button + Your Info + GST ─────────────────── */}
          <div className='flex flex-col gap-4 flex-1 lg:border-r lg:border-grayZl-850 lg:pr-8 lg:pb-8'>
            {/* Desktop-only Back button */}
            <div className='hidden lg:flex items-center'>
              <button
                type='button'
                onClick={() => router.back()}
                className='flex items-center gap-1 text-grayZl-400 hover:text-white text-xs font-medium transition-colors cursor-pointer'
              >
                <ChevronLeftIcon className='w-4 h-4' />
                <span>Back</span>
              </button>
            </div>


            <Form
              form={form}
              layout='vertical'
              className='max-w-full'
              initialValues={{
                phoneCountryCode: 'in',
                phonePrefix: '91',
                isGst: false,
              }}
            >
              {/* Your Info Card */}
              <CheckoutV2Card title='Your Info'>
                <div className='space-y-3.5'>
                  {/* Full name (Height: 44px) */}
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    className='mb-0'
                    name='fullname'
                    rules={[
                      {
                        required: true,
                        message: 'Please enter your full name!',
                      },
                    ]}
                  >
                    <Input
                      className='placeholder:text-grayZl-700 rounded-lg bg-black border-[#424242] text-white hover:border-primary focus:border-primary text-sm h-11'
                      style={{ height: '44px' }}
                      size='large'
                      placeholder='Full name'
                    />
                  </Form.Item>

                  {/* Phone Number (Height: exactly 44px matching name field) */}
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    className='mb-0'
                    name='phone'
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || String(value).trim() === '') {
                            return Promise.reject(
                              new Error('Please input your phone number!'),
                            );
                          }
                          if (!phoneValidRef.current) {
                            return Promise.reject(
                              new Error('Please enter a valid phone number'),
                            );
                          }
                          const prefix =
                            form.getFieldValue('phonePrefix') ||
                            globalCountry?.phoneCode ||
                            '91';
                          const cleanPrefix = String(prefix).replace(/\D/g, '');
                          const cCode = (
                            form.getFieldValue('phoneCountryCode') ||
                            globalCountry?.countryCode ||
                            'in'
                          ).toUpperCase() as CountryCode;
                          const raw = String(value).replace(/\s+/g, '');
                          const fullNum = raw.startsWith('+')
                            ? raw
                            : `+${cleanPrefix}${raw}`;
                          const isDirectValid = isValidPhoneNumber(raw, cCode);
                          const isFullValid = isValidPhoneNumber(fullNum, cCode);
                          if (!isDirectValid && !isFullValid) {
                            return Promise.reject(
                              new Error('Please enter a valid phone number'),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <IntlTelInput
                      placeholder='Enter phone number'
                      autoPlaceholder={false}
                      defaultCountry={phoneCountry || 'in'}
                      value={form.getFieldValue('phone') || ''}
                      style={{ height: '44px', minHeight: '44px', maxHeight: '44px' }}
                      containerClassName='flex intl-tel-input z-10 w-full ant-input ant-input-lg ant-input-outlined checkout-intl-tel-input platform-checkout-phone h-[44px] min-h-[44px]'
                      inputClassName='flex-1 bg-transparent outline-none ant-input ant-input-lg ps-14 placeholder:text-grayZl-700 text-white text-sm h-full'
                      onSelectFlag={(_number, countryData) => {
                        const newIso = countryData.iso2?.toLowerCase() || 'in';
                        const newPrefix = countryData.dialCode || '91';
                        setPhoneCountry(newIso);
                        form.setFieldsValue({
                          phoneCountryCode: newIso,
                          phonePrefix: newPrefix,
                        });
                        form.validateFields(['phone']).catch(() => { });
                      }}
                      onPhoneNumberChange={(isValid, value, countryData) => {
                        const newIso = countryData.iso2?.toLowerCase() || 'in';
                        const newPrefix = countryData.dialCode || '91';
                        phoneValidRef.current = isValid;
                        setIsPhoneValid(isValid);
                        setPhoneCountry(newIso);
                        form.setFieldsValue({
                          phone: value,
                          phoneCountryCode: newIso,
                          phonePrefix: newPrefix,
                        });
                        form.validateFields(['phone']).catch(() => { });
                      }}
                      onPhoneNumberBlur={(isValid, _value, _countryData) => {
                        phoneValidRef.current = isValid;
                        setIsPhoneValid(isValid);
                        form.validateFields(['phone']).catch(() => { });
                      }}
                    />
                  </Form.Item>

                  {/* Email Address (when !token) */}
                  {!token && (
                    <div>
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        className='mb-0'
                        name='email'
                        validateStatus={emailAlreadyExists ? 'error' : undefined}
                        rules={[
                          {
                            required: true,
                            message: 'Please enter your email!',
                          },
                          {
                            type: 'email',
                            message: 'Please enter a valid email address!',
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const domain = value.split('@')[1] || '';
                              if (domain && !disposable.validate(domain)) {
                                return Promise.reject(
                                  new Error(
                                    'Please enter a valid email address!',
                                  ),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          className='placeholder:text-grayZl-700 rounded-lg bg-black border-[#424242] text-white hover:border-primary focus:border-primary text-sm h-11'
                          style={{ height: '44px' }}
                          size='large'
                          onBlur={handleCheckEmailExist}
                          onChange={() => {
                            if (emailAlreadyExists) {
                              setEmailAlreadyExists(false);
                              form.validateFields(['email']).catch(() => { });
                            }
                          }}
                          placeholder='Email address'
                        />
                      </Form.Item>
                      {emailAlreadyExists && (
                        <p
                          className='text-red-500 text-xs mt-1.5 font-medium cursor-pointer'
                          onClick={() => {
                            setAuthModal((prevState) => ({
                              ...prevState,
                              login: true,
                              email: form.getFieldValue('email')?.trim(),
                              emailExistsMessage: true,
                            }));
                          }}
                        >
                          Email already exists,{' '}
                          <span className='underline font-semibold text-primary'>
                            please login
                          </span>
                          .
                        </p>
                      )}
                    </div>
                  )}

                  {/* Create Account Password (when !token) (Height: 44px) */}
                  {!token && (
                    <div>
                      <Form.Item
                        prefixCls={formItemPrefixCls}
                        className='mb-0'
                        name='password'
                        rules={[
                          {
                            required: true,
                            message: 'Please enter your password!',
                          },
                          {
                            min: 8,
                            message: 'Password must be at least 8 characters',
                          },
                        ]}
                      >
                        <Input.Password
                          classNames={{
                            input:
                              'placeholder:text-grayZl-700 text-white text-sm bg-transparent',
                          }}
                          className='rounded-lg bg-black border-[#424242] text-white hover:border-primary focus:border-primary h-11'
                          style={{ height: '44px' }}
                          size='large'
                          placeholder='Create account password'
                          onChange={(e) => {
                            const { strength } = calculatePasswordStrengthV2(
                              e.target.value,
                            );
                            setPasswordStrength(strength);
                          }}
                        />
                      </Form.Item>
                      {passwordStrength > 0 && (
                        <div className='flex gap-1 mt-2'>
                          {[1, 2, 3, 4].map((bar) => (
                            <div
                              key={bar}
                              className='flex-1 h-1 rounded'
                              style={{
                                backgroundColor:
                                  bar <= passwordStrength
                                    ? passwordStrength >= 3
                                      ? '#22c55e'
                                      : passwordStrength === 2
                                        ? '#eab308'
                                        : '#ef4444'
                                    : '#374151',
                                transition: 'background-color 0.3s ease',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hidden phone metadata fields */}
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='phoneCountryCode'
                    hidden
                    initialValue='in'
                  />
                  <Form.Item
                    prefixCls={formItemPrefixCls}
                    name='phonePrefix'
                    hidden
                    initialValue='91'
                  />
                </div>
              </CheckoutV2Card>

              {/* Add GST details Card (No border, balanced vertical padding) */}
              <div className='mt-3.5 bg-grayZl-950 rounded-[12px] px-4 py-2.5 min-h-[44px] flex flex-col justify-center transition-all'>
                <Form.Item
                  prefixCls={formItemPrefixCls}
                  name='isGst'
                  valuePropName='checked'
                  className='!m-0 flex items-center'
                >
                  <Checkbox
                    checked={isGst}
                    onChange={(e) => {
                      setIsGst(e.target.checked);
                      if (!e.target.checked) {
                        form.setFieldsValue({ gstNumber: undefined });
                      }
                    }}
                    className='text-white text-sm font-medium leading-normal flex items-center cursor-pointer m-0'
                  >
                    Add GST details (optional)
                  </Checkbox>
                </Form.Item>
                {isGst && (
                  <div className='mt-3 mb-1'>
                    <Form.Item
                      prefixCls={formItemPrefixCls}
                      name='gstNumber'
                      className='!m-0'
                      validateTrigger={['onChange', 'onBlur']}
                      normalize={(value) =>
                        value ? String(value).toUpperCase() : value
                      }
                      rules={[
                        {
                          validator: async (_, value: string) => {
                            if (!isGst || !value || String(value).trim() === '') {
                              return Promise.resolve();
                            }
                            const cleaned = String(value).trim().toUpperCase();
                            const gstRegex =
                              /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
                            if (!gstRegex.test(cleaned)) {
                              return Promise.reject(
                                new Error('Please enter a valid GSTIN number!'),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder='Add GST details'
                        maxLength={15}
                        className='placeholder:text-grayZl-700 rounded-lg bg-black border-[#424242] text-white hover:border-primary focus:border-primary text-sm h-11 uppercase'
                        style={{ height: '44px' }}
                        size='large'
                        onBlur={() =>
                          form.validateFields(['gstNumber']).catch(() => { })
                        }
                      />
                    </Form.Item>
                  </div>
                )}
              </div>
            </Form>
          </div>

          {/* ─── Right Column: Plan Info + Promo + Summary + Buttons ─────────── */}
          <div className='lg:min-w-[400px] lg:w-[460px] w-full flex flex-col gap-4 lg:pb-8'>
            {/* Desktop-only Plan Header */}
            <div className='hidden lg:block'>
              <PlanHeader />
            </div>

            {/* Promo Code Section (Border around input container) */}
            <div className='space-y-2.5'>
              <div
                className={`flex items-center w-full bg-black rounded-xl p-1.5 transition-colors ${couponError
                  ? 'border border-red-500'
                  : 'border border-grayZl-850 hover:border-grayZl-700 focus-within:border-primary'
                  }`}
              >
                <input
                  type='text'
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    if (couponError) setCouponError(null);
                  }}
                  placeholder='Enter your promo code'
                  className='flex-1 bg-transparent border-none text-white text-sm px-3 py-1 outline-none placeholder:text-grayZl-700 font-medium'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyCoupon();
                    }
                  }}
                />
                <Button
                  id='checkout-v2-coupon-btn'
                  loading={couponLoading}
                  onClick={handleApplyCoupon}
                  style={{ width: '128px', minWidth: '128px', maxWidth: '128px' }}
                  className='bg-[#2A2A2A] hover:bg-[#383838] text-white text-xs font-medium uppercase tracking-wider w-[128px] min-w-[128px] max-w-[128px] h-9 rounded-lg border-none shadow-none flex items-center justify-center shrink-0 cursor-pointer'
                >
                  APPLY
                </Button>
              </div>

              {couponError && (
                <p className='text-red-500 text-xs font-medium -mt-1'>
                  {couponError}
                </p>
              )}

              {appliedCoupon && (
                <div className='inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-grayZl-900 text-white text-xs font-medium'>
                  <TagIcon className='w-4 h-4 text-white shrink-0' />
                  <span className='font-medium'>{appliedCoupon}</span>
                  <button
                    type='button'
                    onClick={handleRemoveCoupon}
                    className='text-grayZl-400 hover:text-white ml-0.5 transition-colors cursor-pointer'
                  >
                    <XIcon className='w-3.5 h-3.5' />
                  </button>
                </div>
              )}
            </div>

            {/* Price Summary Section (No card border) */}
            <div>
              <h3 className='text-xs md:text-sm font-medium uppercase tracking-wider text-white mb-2.5 font-montserrat'>
                PRICE SUMMARY
              </h3>
              <div className='bg-grayZl-950 rounded-[12px] p-4 md:p-5 space-y-3 font-montserrat overflow-hidden'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-grayZl-600'>Cart Total</span>
                  <span className='text-white font-medium'>
                    ₹{cartTotal.toLocaleString('en-IN')}
                  </span>
                </div>

                {appliedCoupon && couponDiscount > 0 && (
                  <div className='flex items-center justify-between text-sm'>
                    <div className='flex items-center gap-2'>
                      <span className='text-grayZl-600'>Discount Applied</span>
                      <span className='bg-grayZl-900 text-[11px] text-grayZl-400 px-2 py-0.5 rounded font-mono font-medium'>
                        {appliedCoupon}
                      </span>
                    </div>
                    <span className='text-white font-medium'>
                      - ₹{couponDiscount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}

                {totalDiscount > 0 && (
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-grayZl-600'>Total Discount</span>
                    <span className='text-white font-medium'>
                      - ₹{totalDiscount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}

                {/* Divider touching left and right card edges */}
                <div className='border-t border-grayZl-850 -mx-4 md:-mx-5 my-3' />

                <div className='flex items-center justify-between'>
                  <span className='text-white font-medium text-base'>To Pay</span>
                  <span className='text-white font-medium text-xl md:text-2xl'>
                    ₹{toPay.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className='flex flex-col gap-2 mt-4'>
                {offerPaymentMethod === 'upi' ? (
                  <Button
                    onClick={() => handleInitiatePayment('upi')}
                    loading={loadingPaymentMethod === 'upi' || isVerifyingPayment}
                    disabled={
                      !submittable ||
                      loadingPaymentMethod !== null ||
                      authLoading ||
                      isVerifyingPayment
                    }
                    className='w-full bg-primary hover:opacity-90 text-white font-medium h-12 rounded-xl text-center text-sm md:text-base border-none transition-all shadow-sm flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Pay with UPI
                  </Button>
                ) : offerPaymentMethod === 'card' ? (
                  <Button
                    onClick={() => handleInitiatePayment('card')}
                    loading={loadingPaymentMethod === 'card' || isVerifyingPayment}
                    disabled={
                      !submittable ||
                      loadingPaymentMethod !== null ||
                      authLoading ||
                      isVerifyingPayment
                    }
                    className='w-full bg-primary hover:opacity-90 text-white font-medium h-12 rounded-xl text-center text-sm md:text-base border-none transition-all shadow-sm flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Pay with Card
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleInitiatePayment()}
                    loading={
                      loadingPaymentMethod === 'continue' || isVerifyingPayment
                    }
                    disabled={
                      !submittable ||
                      loadingPaymentMethod !== null ||
                      authLoading ||
                      isVerifyingPayment
                    }
                    className='w-full bg-primary hover:opacity-90 text-white font-medium h-12 rounded-xl text-center text-sm md:text-base border-none transition-all shadow-sm flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Continue Payment
                  </Button>
                )}
                {isVerifyingPayment && (
                  <p className='text-xs text-center text-grayZl-400 font-medium animate-pulse'>
                    Verifying payment, please do not refresh or close this window...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformCheckout;
